import buildScreenHeaderItems from '@/components/buildScreenHeaderItems'
import EmptyStateIllustration from '@/components/EmptyStateIllustration'
import { Center } from '@/components/ui/center'
import { IconSymbol } from '@/components/ui/IconSymbol'
import { Pressable } from '@/components/ui/pressable'
import { Text } from '@/components/ui/text'
import { cn } from '@/lib/cn'
import { isBelowiOS26 } from '@/lib/utils/iosCompat'
import useJiriHeaderItems from '@/modules/jiri/useJiriHeaderItems'
import useProfileHeaderItems from '@/modules/profile/useProfileHeaderItems'
import TradeOrders from '@/modules/trade/TradeOrders'
import {
  fetchTradeOrdersThunk,
  fetchTradeStatsThunk,
  restoreTradeMerchantType,
  selectCanAccessTrade,
  selectTradeMerchantType,
} from '@/redux/slices/tradeSlice'
import { useAppDispatch, useAppSelector, type RootState } from '@/redux/store'
import { Stack, useFocusEffect, useRouter } from 'expo-router'
import { useColorScheme } from 'nativewind'
import { useCallback, useEffect, useRef } from 'react'
import { Platform } from 'react-native'
import { useStore } from 'react-redux'

export default function TradeOrdersPage() {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const jiriHeaderItems = useJiriHeaderItems()
  const profileHeaderItems = useProfileHeaderItems()
  const { colorScheme } = useColorScheme()
  const labelColor = colorScheme === 'dark' ? '#FFFFFF' : '#000000'

  const store = useStore<RootState>()
  const currentUserLoaded = useAppSelector(state => state.currentUser.loaded)
  const canAccessTrade = useAppSelector(selectCanAccessTrade)
  const merchantTypeRestored = useAppSelector(state => state.trade.merchantTypeRestored)
  const merchantType = useAppSelector(selectTradeMerchantType)
  const duration = useAppSelector(state => state.trade.duration)
  const paidStatus = useAppSelector(state => state.trade.paidStatus)

  const billingAccountId = useAppSelector(state => state.currentUser.user?.billingAccount?.id)
  useEffect(() => {
    dispatch(restoreTradeMerchantType())
  }, [dispatch, billingAccountId])

  const ready = merchantTypeRestored && canAccessTrade && !!merchantType

  // Single load path: runs on focus and again whenever the query changes while focused, so a change
  // made on another screen (e.g. switch_merchant) loads exactly once when this screen regains focus.
  const lastQueryKey = useRef<string | null>(null)
  useFocusEffect(
    useCallback(() => {
      if (!ready) return
      const queryKey = `${merchantType}|${duration}|${paidStatus}`
      const queryChanged = queryKey !== lastQueryKey.current
      lastQueryKey.current = queryKey
      // Coming back from an order's detail must not collapse an already paginated list to page 1.
      if (queryChanged || store.getState().trade.currentPage <= 1) {
        dispatch(fetchTradeOrdersThunk({ page: 1 }))
      }
      dispatch(fetchTradeStatsThunk())
    }, [dispatch, store, ready, merchantType, duration, paidStatus])
  )

  const handleSwitchMerchant = () => router.push('/trade/switch_merchant')

  if (currentUserLoaded && !canAccessTrade) {
    return (
      <>
        <Stack.Screen options={{ ...buildScreenHeaderItems({ left: profileHeaderItems }) }} />
        <Center className="flex-1">
          <EmptyStateIllustration message="你没有查看交易的权限" />
        </Center>
      </>
    )
  }

  return (
    <>
      <Stack.Screen
        options={{
          ...buildScreenHeaderItems({
            left: profileHeaderItems,
            right: [
              Platform.OS === 'ios'
                ? {
                    type: 'custom',
                    element: (
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel="切换商户号"
                        onPress={handleSwitchMerchant}
                        // iOS 26 wraps bar items in a glass capsule; earlier versions need the designed pill drawn
                        className={cn(
                          'flex-row items-center gap-1 px-2',
                          isBelowiOS26 && 'rounded-full bg-secondary-system-background px-3 py-1.5'
                        )}
                      >
                        {isBelowiOS26 && <IconSymbol name="arrow.left.arrow.right" size={12} color={labelColor} />}
                        <Text size="footnote" className="text-label">
                          切换商户号
                        </Text>
                      </Pressable>
                    ),
                  }
                : {
                    type: 'button',
                    label: '切换商户号',
                    icon: { type: 'sfSymbol', name: 'arrow.left.arrow.right' },
                    onPress: handleSwitchMerchant,
                  },
              ...jiriHeaderItems,
            ],
          }),
        }}
      />

      <TradeOrders />
    </>
  )
}
